import React from 'react';
import PropTypes from 'prop-types';

import {cloneDeep} from 'lodash';

import Card from 'common/components/card';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import {Column, Table} from 'common/components/table';
import {formatDate} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import {
  ReportCardHeightTypeEnum,
  ReportDifferenceColorEnum,
  ReportTableDensityEnum,
  ReportTableKeyEnum,
  TimeResolutionEnum,
} from 'common/proxies/enum-proxy';
import {getDateFormatForResolution} from 'common/utils/chart-util';
import {t} from 'common/utils/translation-util';
import {formatUnits} from 'common/utils/units-util';

import './index.scss';

const ALL_COLUMNS = 'all_cols';

// difference label excludes (%), which is used in csv
const DIFFERENCE_LABEL = t('Difference');

const TIMESTAMP_LABELS = Object.values(TimeResolutionEnum).map(
  (_enum) => _enum.displayName,
);

export default class TableCard extends React.Component {
  static propTypes = {
    connectDragSource: PropTypes.func,
    disableConfig: PropTypes.bool,
    report: PropTypes.object.isRequired,
  };

  static defaultProps = {
    connectDragSource: (x) => x,
    disableConfig: false,
  };

  getTimestampIndex(headerRow, timestampLabels) {
    let timestampIndex = -1;
    headerRow.forEach((label, index) => {
      if (timestampLabels.indexOf(label) !== -1) {
        timestampIndex = index;
      }
    });
    return timestampIndex;
  }

  getCardContentClassName(tableDensity) {
    if (tableDensity.id === ReportTableDensityEnum.COMPACT.id) {
      return 'TableCard--compact';
    }
  }

  getCurrencyColumnIndexes(meta) {
    const {currency} = meta;

    let {comparisonMeta} = meta;
    if (!comparisonMeta) {
      comparisonMeta = [];
    }

    const currencyColumnIndexes = [];

    if (currency && !comparisonMeta.length) {
      // all columns use the same unit/currency
      return ALL_COLUMNS;
    } else if (currency) {
      // column 1 is main series (0 is row key)
      currencyColumnIndexes.push(1);
    }

    comparisonMeta.forEach((_meta, index) => {
      if (_meta.currency) {
        currencyColumnIndexes.push(index + 2);
      }
    });

    return currencyColumnIndexes;
  }

  formatDifference(row, differenceIndex, reverseArrowColors) {
    const value = row[differenceIndex];

    let icon = '';
    if (value > 0) {
      icon = 'up';
      if (reverseArrowColors) {
        icon = 'up-green';
      }
    } else if (value < 0) {
      icon = 'down';
      if (reverseArrowColors) {
        icon = 'down-red';
      }
    }

    row[differenceIndex] = {
      icon,
      text: Math.abs(value),
    };
  }

  formatTimestamp(row, timestampIndex, resolution) {
    const timestamp = row[timestampIndex];
    const dateFormattingTokens = getDateFormatForResolution(resolution);
    const shouldKeepTime = !(
      resolution && resolution.slug === TimeResolutionEnum.DAY.slug
    );
    row[timestampIndex] = formatDate(
      timestamp,
      dateFormattingTokens,
      shouldKeepTime,
    );
  }

  formatNumbers(row, timestampIndex, differenceIndex, currencyColumnIndexes) {
    const {meta} = this.props.report;
    const currencyCode = meta.currency?.id || 'USD';

    row.forEach((value, index) => {
      if (value === null || value === '') {
        row[index] = '\u2013'; // show a dash for empty values
      } else if (index === differenceIndex) {
        row[index].text = formatNumber(row[index].text, 'percent');
      } else if (
        index !== 0 &&
        index !== timestampIndex &&
        typeof value === 'number'
      ) {
        let formatKey = 'number';
        if (
          currencyColumnIndexes === ALL_COLUMNS ||
          currencyColumnIndexes.indexOf(index) > -1
        ) {
          formatKey = 'currency';
        }
        row[index] = formatNumber(value, formatKey, null, currencyCode);
      }
    });
  }

  render() {
    const {tableData, meta} = this.props.report;

    // determine which columns are currency
    const currencyColumnIndexes = this.getCurrencyColumnIndexes(meta);

    // determine if difference arrow color should be reversed
    const resourceType = meta.resource && meta.resource.type;
    const {differenceColor} = meta;
    let reverseArrowColors =
      resourceType === 'production' || resourceType === 'recycling';
    if (differenceColor) {
      reverseArrowColors =
        differenceColor.id === ReportDifferenceColorEnum.GREEN_INC__RED_DEC.id;
    }

    // copy and parse table rows
    const data = cloneDeep(tableData);
    const headerRow = data[0];

    const differenceIndex = headerRow.indexOf(
      ReportTableKeyEnum.DIFFERENCE.displayName,
    );
    const timestampIndex = this.getTimestampIndex(headerRow, TIMESTAMP_LABELS);

    let dataRows;
    let summaryRow;
    if (meta.summaryFigure) {
      dataRows = data.slice(1, -1);
      summaryRow = data[data.length - 1];
    } else {
      dataRows = data.slice(1);
    }

    // format rows
    if (timestampIndex > -1) {
      dataRows.forEach((row) =>
        this.formatTimestamp(row, timestampIndex, meta.resolution),
      );
    }

    if (differenceIndex > -1) {
      dataRows.forEach((row) =>
        this.formatDifference(row, differenceIndex, reverseArrowColors),
      );
      if (summaryRow) {
        this.formatDifference(summaryRow, differenceIndex, reverseArrowColors);
      }
    }

    dataRows.forEach((row) =>
      this.formatNumbers(
        row,
        timestampIndex,
        differenceIndex,
        currencyColumnIndexes,
      ),
    );

    if (summaryRow) {
      this.formatNumbers(
        summaryRow,
        timestampIndex,
        differenceIndex,
        currencyColumnIndexes,
      );
    }

    const cardContentClassName = this.getCardContentClassName(
      meta.tableDensity,
    );

    const header = this.props.connectDragSource(
      <div>
        <CardHeader
          dataManagerUrl={meta.dataManagerUrl}
          disableConfig={this.props.disableConfig}
          priorityWarnings={this.props.report.priorityWarnings}
          reportId={this.props.report.id}
          scheduleId={this.props.report.scheduleId}
          subtitle={meta.subtitle || meta.period.displayName}
          title={this.props.report.name}
          warnings={this.props.report.warnings}
        />
      </div>,
    );
    const style =
      meta.cardHeightType === ReportCardHeightTypeEnum.CUSTOM.id
        ? {height: meta.cardHeight}
        : {};
    return (
      <Card className={'TableCard'} loading={false}>
        {header}
        <CardContent className={cardContentClassName} style={style}>
          <Table data={dataRows} summaryData={summaryRow}>
            {headerRow.map((label, index) => (
              <Column key={`${this.props.report.id}-${label}-${index}`}>
                {index === differenceIndex
                  ? DIFFERENCE_LABEL
                  : formatUnits(label)}
              </Column>
            ))}
          </Table>
        </CardContent>
      </Card>
    );
  }
}
